import { Helmet } from "react-helmet-async";

/**
 * Minimal SEO/meta helper.
 * - Sets <title>, <meta description>, OG/Twitter cards.
 * - Optionally injects a JSON-LD <script> for structured data.
 *
 * @param {string} title - <title> text (defaults to "Mystery Served")
 * @param {string} description - <meta description>
 * @param {string} image - absolute URL for og:image / twitter:image
 * @param {string} url - canonical URL for the page
 * @param {object|object[]} schema - JSON-LD structured-data object(s)
 * @param {string} type - og:type (default: "website", use "article" for tour pages)
 */
export default function Seo({
  title,
  description,
  image,
  url,
  schema,
  type = "website",
}) {
  const finalTitle = title ? `${title} · Mystery Served` : "Mystery Served — Immersive Mystery Tours in St. Petersburg, FL";
  const finalDesc =
    description ||
    "Mystery Served is an interactive mystery adventure in St. Petersburg, FL. Follow clues across real streets, visit local venues, and turn the city into your playground.";
  const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "https://mysteryserved.com");
  const finalImg = image || "https://mysteryserved.com/og-default.jpg";

  const schemas = Array.isArray(schema) ? schema : (schema ? [schema] : []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={finalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:image" content={finalImg} />
      <meta property="og:site_name" content="Mystery Served" />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImg} />

      {/* Bot allow-list — explicit for AI crawlers */}
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Global LocalBusiness schema — include on Home page so Google attaches it to the brand.
 */
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://mysteryserved.com/#org",
  name: "Mystery Served",
  url: "https://mysteryserved.com",
  description:
    "Immersive, interactive mystery tour adventures across St. Petersburg, Florida. Follow clues, visit local venues, and turn the city into your playground.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "St. Petersburg",
    addressRegion: "FL",
    addressCountry: "US",
  },
  telephone: "+1-727-270-5531",
  email: "info@mysteryserved.com",
  priceRange: "$$",
  areaServed: { "@type": "City", name: "St. Petersburg, FL" },
};

/**
 * Build a schema.org TouristAttraction/Event entry for a single tour.
 */
export function buildTourSchema(tour, baseUrl = "https://mysteryserved.com") {
  if (!tour) return null;
  return {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: tour.title,
    description: tour.short_description || tour.full_description || tour.title,
    url: `${baseUrl}/tour/${tour.slug}`,
    image: tour.cover_image || tour.gallery?.[0] || undefined,
    address: {
      "@type": "PostalAddress",
      addressLocality: "St. Petersburg",
      addressRegion: "FL",
      addressCountry: "US",
    },
    offers: tour.base_price
      ? {
          "@type": "Offer",
          price: String(tour.base_price),
          priceCurrency: "USD",
          availability: tour.is_active ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
          url: `${baseUrl}/tour/${tour.slug}`,
        }
      : undefined,
  };
}
